import type { PreloadedQuery } from 'react-relay';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { buildNavigationAriForSidebarData } from '@atlassian/global-side-navigation/navigation-ari';
import type { RouteResource } from '@atlassian/react-resource-router';
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import LayoutManagerNavPlatRootStateQuery, {
	type LayoutManagerQuery,
} from './__generated__/LayoutManagerQuery.graphql';

/**
 * When FHS is enabled, the PageLayout component gets passed the defaultCollapsed prop, which causes
 * the other other defaultCollapsed-props to be ignored.
 *
 * The sidebar state is managed in src/packages/platform/controllers-layout:
 *  - Layout Manager (RSS): src/controllers/layout-controller/containers/layout-manager/index.tsx
 *  - JiraPageLayout calls useLeftSidebarState(), which reads ^^^.
 *  - The state is set in the Layout Manager, after fetching this resource, if FHS is on.
 */
export const navPlatformRootStateResource: RouteResource<
	PreloadedQuery<LayoutManagerQuery, Record<string, unknown>>
> = createRelayResource<LayoutManagerQuery>({
	type: 'NAV_PLATFORM_SIDEBAR_UI_STATE',
	// Anonymous users must not trigger this GSN GraphQL query. Use the
	// resource-level `skip` predicate so `loadQuery` is never invoked for them —
	// `getQuery` will not run when `skip` returns `true`.
	skip: (_, { tenantContext: { isAnonymous } }) => {
		return isAnonymous;
	},
	getQuery: (_, { tenantContext: { activationId, atlassianAccountId, cloudId } }) => {
		return {
			parameters: LayoutManagerNavPlatRootStateQuery,
			variables: {
				navPlatSidebarDataEntityAri: buildNavigationAriForSidebarData({
					activationId,
					firstPartyApp: 'jira',
					siteId: cloudId,
				}),
				ownerAri:
					atlassianAccountId && IdentityUserAri.create({ userId: atlassianAccountId }).toString(),
				isAnonymous: false,
			},
			options: { fetchPolicy: 'store-or-network' },
		};
	},
	isPageShellCritical: true,
});
